import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  CircleCheck,
  FileText,
  Landmark,
  NotebookPen,
  Receipt,
  TrendingDown,
  TrendingUp,
  Upload,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BusinessHome } from '@/components/portal/business-home';
import {
  BankAccountsCard,
  DashCard,
  ExpensesCard,
  InvoicesCard,
  Loading,
  ProfitAndLossCard,
  SalesTrendCard,
} from '@/components/dashboard/cards';
import { useBankAccounts } from '@/lib/banking';
import { useMe } from '@/lib/clients';
import {
  DASHBOARD_PERIODS,
  currentVatReturn,
  dashboardRange,
  isDashboardPeriod,
  minorOf,
  percentChange,
  taskView,
  useDashboard,
  visibleTasks,
  type DashboardData,
  type DashboardPeriod,
} from '@/lib/dashboard';
import { todayIso } from '@/lib/dates';
import { minorToPlain } from '@/lib/money';
import { formatAmount, formatDate, formatMoney } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { useBusinessView } from '@/lib/portal';
import { useSalesSummary } from '@/lib/sales';
import { useCompanySettings } from '@/lib/settings';
import { cn } from '@/lib/utils';
import { TONE_CLASS, useVatReturns, vatStatusChip } from '@/lib/vat';
import { monthLabel as gstMonthLabel, useGstMonths } from '@/lib/gst';
import { homeCurrency, isIndia } from '@/lib/org-locale';
import { ltr } from '@/lib/arabic';
import { i18n, useT } from '@/lib/i18n';

export const Route = createFileRoute('/dashboard')({
  component: Home,
  validateSearch: (s: Record<string, unknown>): { period?: DashboardPeriod } => ({
    ...(isDashboardPeriod(s['period']) ? { period: s['period'] } : {}),
  }),
});

/** Client users (and accounting staff previewing the Business view) get the Business view home. */
function Home() {
  return useBusinessView().on ? <BusinessHome /> : <Dashboard />;
}

/** Dashboard › Business overview (document 06 D1): one card per area, each linking to its screen. */
function Dashboard() {
  const orgId = useOrgId();
  const { period = 'this_month' } = Route.useSearch();
  const navigate = useNavigate();
  const company = useCompanySettings();
  const me = useMe();
  const today = todayIso();
  const fyStartMonth = company.data?.advanced.fiscalYearStart ?? 1;
  const ready = company.isSuccess || company.isError;
  const range = ready ? dashboardRange(period, today, fyStartMonth) : null;
  const dash = useDashboard(range);
  const t = useT();

  if (!orgId)
    return (
      <>
        <PageHeader
          title={t('reportPages.dashboard.title')}
          description={t('reportPages.dashboard.overview')}
        />
        <EmptyState title={t('shell.selectClient')} hint={t('reportPages.dashboard.selectHint')} />
      </>
    );

  return (
    <>
      <PageHeader
        title={
          me.data?.name
            ? greeting(me.data.name.split(' ')[0] ?? '')
            : t('reportPages.dashboard.title')
        }
        description={
          company.data
            ? t('reportPages.dashboard.overviewFor', { company: company.data.company.name })
            : t('reportPages.dashboard.overview')
        }
        actions={
          <NativeSelect
            aria-label={t('reportPages.dashboard.periodLabel')}
            className="w-[200px] bg-card"
            value={period}
            onChange={(e) =>
              void navigate({
                to: '/dashboard',
                search: e.target.value === 'this_month' ? {} : { period: e.target.value as never },
              })
            }
          >
            {DASHBOARD_PERIODS.map((p) => (
              <option key={p.key} value={p.key}>
                {t(`periods.${p.key}`)}
              </option>
            ))}
          </NativeSelect>
        }
      />
      {dash.isError ? (
        <EmptyState title={t('reportPages.dashboard.loadError')} hint={dash.error.message} />
      ) : null}
      <KpiStrip data={dash.data} />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="dashboard">
        <ProfitAndLossCard data={dash.data} period={period} />
        <ExpensesCard data={dash.data} />
        <InvoicesCard />
        <BankAccountsCard />
        <SalesTrendCard data={dash.data} />
        {isIndia() ? (
          <GstCard />
        ) : (
          <VatCard today={today} vatRegistered={company.data?.vat.vatRegistered ?? true} />
        )}
        <TasksCard data={dash.data} isStaff={me.data?.isStaff ?? true} />
        <QuickActionsCard />
      </div>
    </>
  );
}

const greeting = (name: string): string => {
  const h = new Date().getHours();
  return h < 12
    ? i18n.t('reportPages.dashboard.greetingMorning', { name })
    : h < 17
      ? i18n.t('reportPages.dashboard.greetingAfternoon', { name })
      : i18n.t('reportPages.dashboard.greetingEvening', { name });
};

/** Headline figures above the cards, as in Jira's summary tiles. */
function KpiStrip({ data }: { data: DashboardData | undefined }) {
  const t = useT();
  const sales = useSalesSummary();
  const banks = useBankAccounts();
  const currency = data?.currency ?? homeCurrency();
  const cash = banks.data
    ?.filter((a) => a.accountType === 'bank')
    .reduce((sum, a) => sum + minorOf(a.homeLedgerBalance), 0n);
  const change = data
    ? percentChange(data.profitAndLoss.netProfit, data.profitAndLoss.previous.netProfit)
    : null;
  const forReview = banks.data?.reduce((n, a) => n + a.forReview, 0);
  const tiles: Array<{
    key: string;
    icon: LucideIcon;
    tone: string;
    label: string;
    value: string | undefined;
    hint?: string;
  }> = [
    {
      key: 'net-profit',
      icon: change !== null && change < 0 ? TrendingDown : TrendingUp,
      tone: 'bg-[#dcfff1] text-success',
      label: t('reportPages.dashboard.kpi.netProfit'),
      value: data ? formatMoney(data.profitAndLoss.netProfit, currency) : undefined,
      ...(change !== null
        ? {
            hint: t('reportPages.dashboard.kpi.changeOnPrevious', {
              change: ltr(`${change > 0 ? '+' : ''}${change.toFixed(1)}%`),
            }),
          }
        : {}),
    },
    {
      key: 'cash',
      icon: Wallet,
      tone: 'bg-brand-violet-50 text-primary',
      label: t('reportPages.dashboard.kpi.cashInBank'),
      value: cash !== undefined ? formatMoney(minorToPlain(cash), currency) : undefined,
      ...(banks.data
        ? { hint: t('reportPages.dashboard.kpi.accounts', { count: banks.data.length }) }
        : {}),
    },
    {
      key: 'owed',
      icon: FileText,
      tone: 'bg-[#fff3eb] text-[#c25100]',
      label: t('reportPages.dashboard.kpi.owedToYou'),
      value: sales.data ? formatMoney(sales.data.openInvoices.amount, currency) : undefined,
      ...(sales.data
        ? {
            hint: t('reportPages.dashboard.kpi.overdueOpen', {
              overdue: sales.data.overdue.count,
              open: sales.data.openInvoices.count,
            }),
          }
        : {}),
    },
    {
      key: 'review',
      icon: Landmark,
      tone: 'bg-[#f3f0ff] text-[#5e4db2]',
      label: t('reportPages.dashboard.kpi.toReview'),
      value: forReview !== undefined ? String(forReview) : undefined,
      hint: t('reportPages.dashboard.kpi.bankWaiting'),
    },
  ];
  return (
    <div
      className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
      data-testid="kpi-strip"
    >
      {tiles.map((tile) => (
        <div key={tile.key} className="flex items-start gap-3 rounded-lg border bg-card p-4">
          <div className={cn('grid size-10 shrink-0 place-items-center rounded-md', tile.tone)}>
            <tile.icon className="size-5" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-muted-foreground">{tile.label}</div>
            {tile.value === undefined ? (
              <div className="mt-1 h-6 w-28 animate-pulse rounded bg-muted" />
            ) : (
              <div className="mt-0.5 truncate text-lg font-bold tnum">{tile.value}</div>
            )}
            {tile.hint ? <div className="text-xs text-muted-foreground">{tile.hint}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

const QUICK_ACTIONS = [
  { icon: FileText, label: 'createInvoice', to: '/sales/invoices/new' },
  { icon: Receipt, label: 'recordExpense', to: '/expenses/expenses/new' },
  { icon: Upload, label: 'importBankStatement', to: '/banking/import' },
  { icon: NotebookPen, label: 'journalEntry', to: '/accounting/journal-entries/new' },
] as const satisfies ReadonlyArray<{ icon: LucideIcon; label: string; to: string }>;

/** Shortcuts to the everyday forms. */
function QuickActionsCard() {
  const t = useT();
  return (
    <Card className="gap-3" data-testid="card-quick-actions">
      <CardHeader>
        <CardTitle className="text-base">{t('reportPages.dashboard.quick.title')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        {QUICK_ACTIONS.map((a) => (
          <Link
            key={a.to}
            to={a.to as never}
            className="flex items-center gap-3 rounded-md border px-3 py-2.5 text-sm font-medium transition hover:border-brand-violet-200 hover:bg-accent hover:text-accent-foreground"
          >
            <span className="grid size-7 place-items-center rounded bg-brand-violet-50">
              <a.icon className="size-4 text-primary" />
            </span>
            {t(`reportPages.dashboard.quick.${a.label}`)}
            <ArrowRight className="rtl:-scale-x-100 ms-auto size-3.5 text-muted-foreground" />
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

/** India clients: the latest month's GSTR-3B position (document 06 T6). */
function GstCard() {
  const t = useT();
  const months = useGstMonths();
  const m = months.data?.months.find((x) => x.documents > 0) ?? months.data?.months[0];
  return (
    <DashCard
      title={t('reportPages.dashboard.gst.title')}
      to={m ? '/taxes/gst/gstr3b' : '/taxes/gst'}
      {...(m ? { search: { period: m.period } } : {})}
      linkLabel={
        m ? t('reportPages.dashboard.gst.viewGstr3b') : t('reportPages.dashboard.gst.goToGst')
      }
      testId="card-gst"
    >
      {!months.data ? (
        <Loading />
      ) : !m ? (
        <p className="text-muted-foreground">{t('reportPages.dashboard.gst.none')}</p>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">
            {t('reportPages.dashboard.gst.cashPayable', { month: gstMonthLabel(m.period) })}
          </div>
          <div className="tnum text-2xl font-semibold" data-testid="gst-cash">
            {homeCurrency()} {formatAmount(m.cashPayable)}
          </div>
          <dl className="grid grid-cols-2 gap-1 text-xs">
            <dt className="text-muted-foreground">{t('reportPages.dashboard.gst.outputTax')}</dt>
            <dd className="tnum text-end">{formatAmount(m.outputTax)}</dd>
            <dt className="text-muted-foreground">{t('reportPages.dashboard.gst.itc')}</dt>
            <dd className="tnum text-end">{formatAmount(m.itc)}</dd>
            <dt className="text-muted-foreground">{t('reportPages.dashboard.gst.gstr1Due')}</dt>
            <dd className="text-end">{formatDate(m.gstr1Due)}</dd>
            <dt className="text-muted-foreground">{t('reportPages.dashboard.gst.gstr3bDue')}</dt>
            <dd className="text-end">{formatDate(m.gstr3bDue)}</dd>
          </dl>
        </div>
      )}
    </DashCard>
  );
}

function VatCard({ today, vatRegistered }: { today: string; vatRegistered: boolean }) {
  const t = useT();
  const returns = useVatReturns();
  const r = returns.data ? currentVatReturn(returns.data) : null;
  if (!vatRegistered)
    return (
      <DashCard
        title={t('reportPages.dashboard.vat.title')}
        to="/settings/company"
        linkLabel={t('reportPages.dashboard.vat.companySettings')}
        testId="card-vat"
      >
        <p className="text-muted-foreground">{t('reportPages.dashboard.vat.notRegistered')}</p>
      </DashCard>
    );
  const chip = r ? vatStatusChip(r, today) : null;
  const chipLabel = (): string => {
    if (!r || !chip) return '';
    switch (r.status) {
      case 'open':
        return t('reportPages.dashboard.vat.chipOpen');
      case 'due': {
        const days = Math.round((Date.parse(r.dueDate) - Date.parse(today)) / 86_400_000);
        return days === 0
          ? t('reportPages.dashboard.vat.chipDueToday')
          : t('reportPages.dashboard.vat.chipDueIn', { count: days });
      }
      case 'overdue':
        return t('reportPages.dashboard.vat.chipOverdue');
      case 'filed':
        return t('reportPages.dashboard.vat.chipFiled');
      case 'paid':
        return t('reportPages.dashboard.vat.chipPaid');
    }
  };
  const refundable = r?.netVat.startsWith('-') ?? false;
  return (
    <DashCard
      title={t('reportPages.dashboard.vat.title')}
      to={r ? `/taxes/vat/returns/${r.key}` : '/taxes/vat'}
      linkLabel={
        r
          ? r.status === 'open'
            ? t('reportPages.dashboard.vat.viewReturn')
            : t('reportPages.dashboard.vat.prepareReturn')
          : t('reportPages.dashboard.vat.goToVat')
      }
      testId="card-vat"
    >
      {!returns.data ? (
        <Loading />
      ) : !r ? (
        <p className="text-muted-foreground">{t('reportPages.dashboard.vat.allFiled')}</p>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{r.label}</span>
            {chip ? (
              <span className={cn('rounded-full border px-2 text-xs', TONE_CLASS[chip.tone])}>
                {chipLabel()}
              </span>
            ) : null}
          </div>
          <div>
            <div className="text-xs text-muted-foreground">
              {r.status === 'open'
                ? refundable
                  ? t('reportPages.dashboard.vat.netVatSoFarRefundable')
                  : t('reportPages.dashboard.vat.netVatSoFarPayable')
                : refundable
                  ? t('reportPages.dashboard.vat.netVatRefundable')
                  : t('reportPages.dashboard.vat.netVatPayable')}
            </div>
            <div className="tnum text-2xl font-semibold" data-testid="vat-net">
              {formatMoney(r.netVat, 'AED')}
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-1 text-xs">
            <dt className="text-muted-foreground">{t('reportPages.dashboard.vat.vatOnSales')}</dt>
            <dd className="tnum text-end">{formatAmount(r.outputVat)}</dd>
            <dt className="text-muted-foreground">
              {t('reportPages.dashboard.vat.vatOnPurchases')}
            </dt>
            <dd className="tnum text-end">{formatAmount(r.inputVat)}</dd>
            <dt className="text-muted-foreground">{t('reportPages.dashboard.vat.dueDate')}</dt>
            <dd className="text-end">{formatDate(r.dueDate)}</dd>
          </dl>
          {returns.data.filter((x) => x.status === 'overdue').length > 1 ? (
            <p className="text-xs text-destructive">
              {t('reportPages.dashboard.vat.overdueReturns', {
                count: returns.data.filter((x) => x.status === 'overdue').length,
              })}
            </p>
          ) : null}
        </div>
      )}
    </DashCard>
  );
}

/** Catalogue key of each task's wording (taskView keeps the English for the tests). */
const TASK_KEY = {
  sales_drafts: 'salesDrafts',
  purchase_drafts: 'purchaseDrafts',
  bank_review: 'bankReview',
  periods_to_lock: 'periodsToLock',
  recurring_due: 'recurringDue',
  einvoice_exceptions: 'einvoiceExceptions',
} as const satisfies Record<DashboardData['tasks'][number]['key'], string>;

function TasksCard({ data, isStaff }: { data: DashboardData | undefined; isStaff: boolean }) {
  const t = useT();
  const tasks = data ? visibleTasks(data.tasks, isStaff) : [];
  return (
    <DashCard
      title={t('reportPages.dashboard.tasks.title')}
      to="/accounting/periods"
      linkLabel={t('reportPages.dashboard.tasks.periodsLink')}
      testId="card-tasks"
      className="xl:col-span-2"
    >
      {!data ? (
        <Loading />
      ) : tasks.length === 0 ? (
        <p className="flex items-center gap-2 text-muted-foreground">
          <CircleCheck className="size-4 text-[#1E7F4F]" />{' '}
          {t('reportPages.dashboard.tasks.nothing')}
        </p>
      ) : (
        <ul className="divide-y">
          {tasks.map((task) => {
            const v = taskView(task)!;
            return (
              <li key={task.key} className="flex items-center justify-between gap-2 py-2">
                <div>
                  <Link
                    to={v.to as never}
                    {...(v.search ? { search: v.search as never } : {})}
                    className="font-medium text-primary hover:underline"
                    data-testid={`task-${task.key}`}
                  >
                    {t(`reportPages.dashboard.tasks.${TASK_KEY[task.key]}`, { count: task.count })}
                  </Link>
                  {task.amount && task.amount !== '0.00' ? (
                    <div className="tnum text-xs text-muted-foreground">
                      {formatMoney(task.amount, data.currency)}
                    </div>
                  ) : null}
                  {task.through ? (
                    <div className="text-xs text-muted-foreground">
                      {t('reportPages.dashboard.tasks.through', {
                        date: ltr(formatDate(task.through)),
                      })}
                    </div>
                  ) : null}
                </div>
                <span className="tnum rounded-full bg-muted px-2 text-xs">{task.count}</span>
              </li>
            );
          })}
        </ul>
      )}
    </DashCard>
  );
}
